import React, { useEffect, useLayoutEffect, useRef, useState, useCallback } from "react";
import {
    TableOutlined,
    AppstoreOutlined,
    EditOutlined,
    DeleteOutlined,
    EyeOutlined,
    UserAddOutlined,
    ReloadOutlined,
    SearchOutlined,
} from "@ant-design/icons";
import { Pagination, Button, Tooltip, Input, Spin, message } from "antd";
import { Users } from "lucide-react";
import { useGlobalDrawer } from "../../providers/DriverProvider/DriverProvider";
import UserDetailsLayer from "../../layers/UserDetailsLayer/UserDetailsLayer";
import usersApi from "../../api/usersApi";
import AddUserModal from "../AddUserModal/AddUserModal";

function cn(...classes) {
    return classes.filter(Boolean).join(" ");
}

const UsersList = () => {
    const [users, setUsers] = useState([]);
    const [filteredUsers, setFilteredUsers] = useState([]);
    const [loading, setLoading] = useState(true);
    const [viewMode, setViewMode] = useState("list");
    const [currentPage, setCurrentPage] = useState(1);
    const [searchQuery, setSearchQuery] = useState("");
    const [isModalOpen, setIsModalOpen] = useState(false);
    const [editingUser, setEditingUser] = useState(null);
    const { openDrawer } = useGlobalDrawer();

    /** Сколько строк показывать на странице — от высоты видимой области таблицы */
    const [pageSize, setPageSize] = useState(10);
    const tableBodyHostRef = useRef(null);
    const gridBodyHostRef = useRef(null);

    const LIST_THEAD_PX = 50;
    const LIST_ROW_PX = 52;
    const GRID_CARD_MIN_W = 240;
    const GRID_CARD_H = 168;

    const recalcPageSize = useCallback(() => {
        if (viewMode === "list") {
            const el = tableBodyHostRef.current;
            if (!el) return;
            const h = el.clientHeight;
            if (h < LIST_THEAD_PX + LIST_ROW_PX) return;
            const rows = Math.floor((h - LIST_THEAD_PX) / LIST_ROW_PX);
            setPageSize(Math.max(5, Math.min(200, rows)));
        } else {
            const el = gridBodyHostRef.current;
            if (!el) return;
            const h = el.clientHeight;
            const w = el.clientWidth;
            if (h < GRID_CARD_H || w < 120) return;
            const cols = Math.max(1, Math.floor(w / GRID_CARD_MIN_W));
            const rowCount = Math.max(1, Math.floor(h / GRID_CARD_H));
            const cells = cols * rowCount;
            setPageSize(Math.max(4, Math.min(200, cells)));
        }
    }, [viewMode]);

    useEffect(() => {
        reloadUsers();
    }, []);

    useLayoutEffect(() => {
        if (loading) return;
        const tick = () => {
            requestAnimationFrame(() => {
                requestAnimationFrame(recalcPageSize);
            });
        };
        tick();
    }, [loading, viewMode, filteredUsers.length, recalcPageSize]);

    useEffect(() => {
        if (loading) return;
        const el =
            viewMode === "list" ? tableBodyHostRef.current : gridBodyHostRef.current;
        if (!el || typeof ResizeObserver === "undefined") {
            window.addEventListener("resize", recalcPageSize);
            return () => window.removeEventListener("resize", recalcPageSize);
        }
        const ro = new ResizeObserver(() => recalcPageSize());
        ro.observe(el);
        window.addEventListener("resize", recalcPageSize);
        return () => {
            ro.disconnect();
            window.removeEventListener("resize", recalcPageSize);
        };
    }, [loading, viewMode, recalcPageSize]);

    const reloadUsers = async () => {
        try {
            setLoading(true);
            const data = await usersApi.getAll();
            setUsers(data);
            setFilteredUsers(data);
            setSearchQuery("");
            setCurrentPage(1);
        } catch (error) {
            console.error("Ошибка обновления списка пользователей:", error);
        } finally {
            setLoading(false);
        }
    };

    const userDelete = async (id) => {
        try {
            await usersApi.delete(id);
            reloadUsers();
        } catch (e) {
            message.error("Что-то пошло не так при удалении пользователя");
        }
    };

    const userDetail = (user) => {
        openDrawer(<UserDetailsLayer user={user} />, { width: 440 });
    };

    const handleSearch = (e) => {
        const query = e.target.value.toLowerCase();
        setSearchQuery(query);
        setCurrentPage(1);

        if (!query) {
            setFilteredUsers(users);
        } else {
            const filtered = users.filter(
                (user) =>
                    `${user.firstName} ${user.lastName}`.toLowerCase().includes(query) ||
                    (user.telegramId && user.telegramId.includes(query))
            );
            setFilteredUsers(filtered);
        }
    };

    const handleAddUser = async (values) => {
        try {
            setLoading(true);
            if (editingUser) {
                await usersApi.update(editingUser.id, values);
            } else {
                await usersApi.create(values);
            }
            reloadUsers();
            setIsModalOpen(false);
            setEditingUser(null);
        } catch (error) {
            console.error("Ошибка сохранения пользователя:", error);
        } finally {
            setLoading(false);
        }
    };

    const handleEditUser = (user) => {
        setEditingUser(user);
        setIsModalOpen(true);
    };

    const paginatedUsers = filteredUsers.slice((currentPage - 1) * pageSize, currentPage * pageSize);

    const totalPages = Math.max(1, Math.ceil(filteredUsers.length / pageSize) || 1);

    useEffect(() => {
        if (currentPage > totalPages) setCurrentPage(totalPages);
    }, [currentPage, totalPages]);

    const shellClass =
        "relative overflow-hidden rounded-2xl border border-white/10 bg-white/[0.04] backdrop-blur-xl shadow-[0_18px_60px_-30px_rgba(0,0,0,0.9)]";

    return (
        <div className="flex min-h-0 flex-1 flex-col gap-4 p-4 text-white sm:p-5">
            {/* Заголовок + тулбар */}
            <section className={cn(shellClass, "shrink-0 p-4 sm:p-5")}>
                <div className="pointer-events-none absolute -right-20 -top-20 h-40 w-40 rounded-full bg-white/5 blur-2xl" />
                <div className="relative flex flex-col gap-4 lg:flex-row lg:items-center lg:justify-between">
                    <div className="flex min-w-0 items-center gap-3">
                        <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl border border-white/10 bg-white/[0.06]">
                            <Users className="h-5 w-5 text-white/85" />
                        </span>
                        <div className="min-w-0">
                            <h1 className="text-lg font-semibold tracking-tight text-white/90 sm:text-xl">Users</h1>
                            <p className="text-[12px] text-white/45">
                                {filteredUsers.length} {filteredUsers.length === 1 ? "user" : "users"}
                                {searchQuery ? " · filtered" : ""}
                            </p>
                        </div>
                    </div>

                    <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center">
                        <Input
                            allowClear
                            prefix={<SearchOutlined className="text-white/35" />}
                            placeholder="Search by name or Telegram ID"
                            value={searchQuery}
                            onChange={handleSearch}
                            className="w-full min-w-[200px] max-w-md !rounded-xl !border-white/10 !bg-white/[0.06] py-2 text-white/90 placeholder:!text-white/35 hover:!border-white/[0.18] focus:!border-emerald-400/50"
                        />
                        <div className="flex flex-wrap items-center gap-2">
                            <div className="flex rounded-xl border border-white/10 bg-white/[0.04] p-0.5">
                                <Button
                                    type="text"
                                    icon={<TableOutlined />}
                                    onClick={() => setViewMode("list")}
                                    className={cn(
                                        "!rounded-lg !h-9",
                                        viewMode === "list"
                                            ? "!bg-white/[0.12] !text-white"
                                            : "!text-white/55 hover:!text-white/85"
                                    )}
                                />
                                <Button
                                    type="text"
                                    icon={<AppstoreOutlined />}
                                    onClick={() => setViewMode("grid")}
                                    className={cn(
                                        "!rounded-lg !h-9",
                                        viewMode === "grid"
                                            ? "!bg-white/[0.12] !text-white"
                                            : "!text-white/55 hover:!text-white/85"
                                    )}
                                />
                            </div>
                            <Button
                                onClick={reloadUsers}
                                icon={<ReloadOutlined />}
                                className="!rounded-xl !border-white/10 !bg-white/[0.06] !text-white/80 hover:!border-white/20 hover:!bg-white/[0.1] hover:!text-white"
                            >
                                Reload
                            </Button>
                            <Button
                                type="primary"
                                onClick={() => {
                                    setEditingUser(null);
                                    setIsModalOpen(true);
                                }}
                                icon={<UserAddOutlined />}
                                className="!rounded-xl !border-0 !bg-emerald-500 !text-[#070A12] hover:!bg-emerald-400"
                            >
                                Add User
                            </Button>
                        </div>
                    </div>
                </div>
            </section>

            {/* Пагинация сверху */}
            <div
                className={cn(
                    shellClass,
                    "flex shrink-0 flex-col gap-3 px-4 py-3 sm:flex-row sm:items-center sm:justify-between sm:px-5"
                )}
            >
                <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:gap-4">
                    <span className="text-[13px] text-white/50">
                        Page {currentPage} of {totalPages}
                    </span>
                    <span className="text-[12px] text-white/35">
                        {viewMode === "list" ? "Rows per page" : "Cards per page"}: {pageSize} (auto)
                    </span>
                </div>
                <Pagination
                    current={currentPage}
                    pageSize={pageSize}
                    total={filteredUsers.length}
                    onChange={(page) => setCurrentPage(page)}
                    showSizeChanger={false}
                    showQuickJumper={filteredUsers.length > pageSize * 15}
                    className="pagination-custom !mb-0 flex flex-wrap justify-end"
                    size="small"
                />
            </div>

            {/* Основная область — тянется на всю высоту экрана */}
            <section className={cn(shellClass, "flex min-h-0 flex-1 flex-col")}>
                {loading ? (
                    <div className="flex flex-1 items-center justify-center py-24">
                        <Spin size="large" className="[&_.ant-spin-dot-item]:!bg-emerald-400" />
                    </div>
                ) : viewMode === "list" ? (
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        <div ref={tableBodyHostRef} className="min-h-0 flex-1 overflow-auto">
                            <table className="h-full min-h-full w-full min-w-[720px] border-collapse text-left text-[13px]">
                                <thead className="sticky top-0 z-[1] bg-[#0a0e18]/95 text-white/70 backdrop-blur-md">
                                    <tr className="border-b border-white/10">
                                        <th className="whitespace-nowrap px-4 py-3.5 font-semibold">ID</th>
                                        <th className="whitespace-nowrap px-4 py-3.5 font-semibold">Name</th>
                                        <th className="whitespace-nowrap px-4 py-3.5 font-semibold">Telegram ID</th>
                                        <th className="whitespace-nowrap px-4 py-3.5 font-semibold">Role</th>
                                        <th className="whitespace-nowrap px-4 py-3.5 font-semibold">Created At</th>
                                        <th className="whitespace-nowrap px-4 py-3.5 font-semibold text-right">Actions</th>
                                    </tr>
                                </thead>
                                <tbody className="text-white/85">
                                    {paginatedUsers.length === 0 ? (
                                        <tr>
                                            <td colSpan={6} className="px-4 py-16 text-center text-white/45">
                                                No users match your search.
                                            </td>
                                        </tr>
                                    ) : (
                                        paginatedUsers.map((user) => (
                                            <tr
                                                key={user.id}
                                                className="border-b border-white/[0.06] transition-colors hover:bg-white/[0.04]"
                                            >
                                                <td className="whitespace-nowrap px-4 py-3.5 text-white/60">{user.id}</td>
                                                <td className="px-4 py-3.5 font-medium text-white/90">{`${user.firstName} ${user.lastName}`}</td>
                                                <td className="whitespace-nowrap px-4 py-3.5 font-mono text-[12px] text-white/65">
                                                    {user.telegramId || "—"}
                                                </td>
                                                <td className="whitespace-nowrap px-4 py-3.5">
                                                    <span className="inline-flex rounded-full border border-white/10 bg-white/[0.06] px-2.5 py-0.5 text-[12px] capitalize text-white/75">
                                                        {user.role}
                                                    </span>
                                                </td>
                                                <td className="whitespace-nowrap px-4 py-3.5 text-white/55">
                                                    {new Date(user.createdAt).toLocaleDateString()}
                                                </td>
                                                <td className="whitespace-nowrap px-4 py-3 text-right">
                                                    <div className="inline-flex gap-0.5">
                                                        <Tooltip title="Show">
                                                            <Button
                                                                onClick={() => userDetail(user)}
                                                                icon={<EyeOutlined className="text-sky-400" />}
                                                                type="text"
                                                                className="!text-white/70 hover:!bg-white/10"
                                                            />
                                                        </Tooltip>
                                                        <Tooltip title="Edit">
                                                            <Button
                                                                onClick={() => handleEditUser(user)}
                                                                icon={<EditOutlined className="text-amber-300/90" />}
                                                                type="text"
                                                                className="!text-white/70 hover:!bg-white/10"
                                                            />
                                                        </Tooltip>
                                                        <Tooltip title="Delete">
                                                            <Button
                                                                onClick={() => userDelete(user.id)}
                                                                icon={<DeleteOutlined className="text-rose-400/90" />}
                                                                type="text"
                                                                className="!text-white/70 hover:!bg-white/10"
                                                            />
                                                        </Tooltip>
                                                    </div>
                                                </td>
                                            </tr>
                                        ))
                                    )}
                                </tbody>
                            </table>
                        </div>
                    </div>
                ) : (
                    <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
                        <div
                            ref={gridBodyHostRef}
                            className="min-h-0 flex-1 overflow-auto p-4 sm:p-5"
                        >
                            <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 2xl:grid-cols-5">
                                {paginatedUsers.map((user) => (
                                    <div
                                        key={user.id}
                                        className="flex flex-col gap-3 rounded-xl border border-white/10 bg-white/[0.04] p-4 transition-colors hover:border-white/[0.14] hover:bg-white/[0.06]"
                                    >
                                        <h3 className="text-[15px] font-semibold text-white/90">{`${user.firstName} ${user.lastName}`}</h3>
                                        <p className="text-[12px] text-white/45">Telegram: {user.telegramId || "—"}</p>
                                        <p className="text-[12px] text-white/45 capitalize">Role: {user.role}</p>
                                        <p className="text-[12px] text-white/45">
                                            Created: {new Date(user.createdAt).toLocaleDateString()}
                                        </p>
                                        <div className="mt-auto flex gap-1 border-t border-white/10 pt-3">
                                            <Tooltip title="Show">
                                                <Button
                                                    onClick={() => userDetail(user)}
                                                    icon={<EyeOutlined className="text-sky-400" />}
                                                    type="text"
                                                />
                                            </Tooltip>
                                            <Tooltip title="Edit">
                                                <Button
                                                    onClick={() => handleEditUser(user)}
                                                    icon={<EditOutlined className="text-amber-300/90" />}
                                                    type="text"
                                                />
                                            </Tooltip>
                                            <Tooltip title="Delete">
                                                <Button
                                                    onClick={() => userDelete(user.id)}
                                                    icon={<DeleteOutlined className="text-rose-400/90" />}
                                                    type="text"
                                                />
                                            </Tooltip>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>
                )}
            </section>

            <AddUserModal
                isModalOpen={isModalOpen}
                onCancel={() => setIsModalOpen(false)}
                onAddUser={handleAddUser}
                loading={loading}
                initialValues={editingUser}
            />
        </div>
    );
};

export default UsersList;
